import {
  CustomEventEmptyState,
  DateRangeEmptyState,
  DeactivatedEmptyState,
  DiscardedEmptyState,
  ListsEmptyState,
} from 'ui/illustrations';
import { zIndex } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { EmptyStateConfig } from './types';

// Values subject to change
export const CUSTOM_EVENTS_GRIDS = `
  auto
  minmax(${pxToRem(160)}, 1fr)
  ${pxToRem(154)}
  ${pxToRem(102)}
  ${pxToRem(144)}
  ${pxToRem(168)}
  ${pxToRem(40)}
`;

export const CUSTOM_EVENTS_TABLE_NAMES = [
  'eventName',
  'lastSeen',
  'docCount',
  'references',
  'retentionPolicy',
];

export const CUSTOM_EVENTS_TABLE_LABELS: { [name: string]: string } = Object.freeze({
  eventName: 'Name',
  lastSeen: 'Last seen',
  docCount: 'Volume',
  references: 'References',
  retentionPolicy: 'Retention period',
});

export const CUSTOM_EVENTS_CSV_TABLE_LABELS: { [name: string]: string } = Object.freeze({
  eventName: 'Event Name',
  docCount: 'Event Count',
  lastSeen: 'Last Seen (UTC)',
  listReferences: 'List References',
  workflowReferences: 'Journey References',
  retentionPolicy: 'Retention Period (days)',
});

export const CSV_COPY: { [name: string]: string } = Object.freeze({
  optionDisplayText: 'Export event summary to CSV',
  fileName: 'custom-event-usage',
  id: 'export-to-csv',
  successMessage: 'Event summary successfully exported. Please check your downloads.',
  failureMessage: 'Event summary could not be exported. Please try again.',
});

export const BLOCK_UNKNOWN_EVENTS_ACTION_MENU_TEXT: { [name: string]: string } = Object.freeze({
  optionDisplayText: 'Block unknown events',
  descriptionText: 'Prevent new event definitions from being added to this project',
  id: 'block-events',
});

export const ALLOW_EVENTS_ACTION_MENU_TEXT: { [name: string]: string } = Object.freeze({
  optionDisplayText: 'Stop blocking unknown events',
  descriptionText: 'Allow new custom events to be added to this project',
  id: 'allow-events',
});

export const DATE_RANGE_QUERY_PARAMS: { [name: string]: string } = Object.freeze({
  start: 'start',
  end: 'end',
  preset: 'preset',
  timezone: 'timezone',
});

export const DEBOUNCE_TIMEOUTS_MS: { [name: string]: number } = Object.freeze({
  search: 500,
  fetchCustomEvents: 1500,
  fetchMappings: 500,
});

export const DISPLAY_TEXT: { [name: string]: string } = Object.freeze({
  searchPlaceholder: 'Search events',
});

export const RETENTION_PERIOD_RADIO_OPTIONS = [
  { id: 'unlimited', name: 'Unlimited (Default)', value: '-1' },
  { id: 'biannually', name: '180 days', value: '180' },
  { id: 'annually', name: '1 year', value: '365' },
  { id: 'every-two-years', name: '2 years', value: '730' },
  // logic choice to use -1 as "other" so that it can be checked and then look for the input/select values
  { id: 'other', name: 'Other', value: '999999' },
];

export const RETENTION_PERIOD_SELECT_OPTIONS = [
  {
    badgeText: '',
    id: 'days',
    Prefix: null,
    Suffix: null,
    label: 'days',
    value: 'days',
  },
  {
    badgeText: '',
    id: 'years',
    Prefix: null,
    Suffix: null,
    label: 'years',
    value: 'years',
  },
];

export const RETENTION_PERIOD_ERROR_MESSAGES = {
  MIN: '30 days is the minimum retention period.',
  MAX: 'If you need a retention period longer than 3,650 days please select Unlimited from the menu.',
};

export const DELETION_POLICY_CUSTOM_EVENT_EVENT_TYPE = 'CustomEventPolicy';

export const SEARCH_QUERY_PARAM = 'search';

export const GROUP_SELECT_Z_INDEX = zIndex.dropdown;
export const DATE_PICKER_Z_INDEX = zIndex.dropdown + 1;

export const TABLE_PAGE_SIZES = [10, 20, 50, 100, 200];

export const SAVED_PERMISSION = {
  allowed: true,
  saved: true,
};
export const DISCARDED_PERMISSION = {
  allowed: true,
  saved: false,
};
export const DEACTIVATED_PERMISSION = {
  allowed: false,
  saved: false,
};

export const EMPTY_STATE_CONFIG: Record<string, EmptyStateConfig> = Object.freeze({
  deactivated: {
    Icon: DeactivatedEmptyState,
    title: "You'll find your deactivated events here",
    subtitle:
      'New instances of deactivated events will be blocked from entering Iterable and hidden from appearing and being used throughout the application.',
  },
  dateRange: {
    Icon: DateRangeEmptyState,
    title: 'No matching events',
    subtitle: 'Try filtering by a different date range.',
  },
  default: {
    Icon: CustomEventEmptyState,
    title: "You'll find your custom events here",
    subtitle:
      'Add a new custom event to personalize segments, campaigns, and journey based on your contact’s behaviors.',
  },
  discarded: {
    Icon: DiscardedEmptyState,
    title: "You'll find your discarded events here",
    subtitle:
      'New instances of discarded events will not be stored, but the event will remain visible throughout the application and can still kick off actions like triggering journeys.',
  },
  search: {
    Icon: ListsEmptyState,
    title: 'No matching events',
    subtitle: 'Try searching by a different name.',
  },
});
